import { useEffect, useRef, useState } from "react";
import { Breakpoint, Breakpoints, ScreenWidthMediaQueryPreset } from "../components/types";
import { matchMediaQueryPreset } from "../utils";

/**
 * @todo [BDS-533] Check and optimize.
 * Ideally, it should do the matchMediaQuery check once when the hook runs for the very first time. And use that
 * result as the initial state value. The way it is now, it most likely makes the consumer of this hook render twice
 * at startup. Also the `handleMQLUpdate` handler should be declared outside the `useEffect` using `useCallback`. It
 * is unnecessarily getting re-initialized each time the `useEffect` runs (which is each time the mql event fires).
 * This will also remove `matchResult` as a hook-dep for the `useEffect` and we may actually get away with running the
 * hook just once, on first execution, and not every time the mql event fires and the `matchResult` state is updated.
 */
export const useMatchMediaQueryPreset = (
  preset: ScreenWidthMediaQueryPreset,
  breakpoints: Breakpoints,
  desktopBreakpoint: Breakpoint,
  defaultMatchResult: boolean = false
) => {
  const [matchResult, setMatchResult] = useState(defaultMatchResult);
  const mqlRef = useRef<MediaQueryList>();

  useEffect(() => {
    const handleMQLUpdate = () => {
      const newResult = !!mqlRef?.current?.matches;
      if (newResult !== matchResult) {
        setMatchResult(newResult);
      }
    };

    // If the preset passed is nullish, this hook will have no effect other than to act as a container for the
    // `matchMediaResult` state that will remain set to the `defaultMatchResult` value.
    if (preset) {
      const mql = matchMediaQueryPreset(preset, breakpoints, desktopBreakpoint);

      // It's possible that the MediaQuery object is undefined.
      // If so, there nothing to do.
      if (mql) {
        setMatchResult(mql.matches);

        // We also need to setup a listener to update this result whenever the evaluated MQL notifies us of a change.
        // This handles the scenario where the screen size changes after initial render.
        mql.addListener(handleMQLUpdate);
        mqlRef.current = mql;
      }
    }

    return () => {
      if (mqlRef && mqlRef.current) {
        mqlRef.current.removeListener(handleMQLUpdate);
      }
    };
  }, [breakpoints, desktopBreakpoint, matchResult, preset]);

  return matchResult;
};
